import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  AgentArchitecture,
  AssistantKey,
  getAssistant$,
  GetAssistantResponse,
  GetTestRunV2Request,
  TestResult,
  TestRun,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import {
  formatCallDuration,
  formatModelAndReasoningFromTestResult,
  getAgentArchitectureName,
  getRunName,
  isVoiceTest,
  StatusMap,
} from '../test-run.utils';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { AvailableModelsService } from '../available-models.service';
import { AssistantKeyResolverService } from '../../../core/services/assistant-key-resolver.service';
import {
  GalaxyColumnDef,
  GalaxyDataSource,
  GalaxyTableModule,
  PagedListRequestInterface,
  PagedResponseInterface,
} from '@vendasta/galaxy/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { TestManagementService } from '../test-management.service';
import { MatDialog } from '@angular/material/dialog';
import { TestResultsModalComponent } from '../test-results-modal/test-results-modal.component';
import { AssistantNamePipe } from '../../../core/utils/namespace-utils';
import { BusinessNameService } from '../../../core/services/business-name.service';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { TranslateService } from '@ngx-translate/core';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';

@Component({
  selector: 'ai-test-run-display',

  imports: [
    CommonModule,
    GalaxyPageModule,
    MatCardModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    GalaxyTableModule,
    MatTableModule,
    MatSortModule,
    GalaxyTooltipModule,
    GalaxyEmptyStateModule,
    AiAssistantI18nModule,
  ],
  providers: [AssistantKeyResolverService],
  templateUrl: './test-run-display.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './test-run-display.component.scss',
})
export class TestRunDisplayComponent implements OnInit {
  private readonly testService = inject(TestManagementService);
  private activatedRoute = inject(ActivatedRoute);
  statusMap = StatusMap;
  protected readonly getRunName = getRunName;
  private readonly dialog = inject(MatDialog);
  private retry$$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  private readonly businessNameService = inject(BusinessNameService);
  private readonly availableModelsService = inject(AvailableModelsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackbarService = inject(SnackbarService);
  private readonly assistantKeyResolver = inject(AssistantKeyResolverService);
  private readonly translateService = inject(TranslateService);

  businessName = signal<string>('');

  readonly models = toSignal(this.availableModelsService.getModelNameLookup());

  private testRunKey$: Observable<GetTestRunV2Request> = this.activatedRoute.paramMap.pipe(
    switchMap((paramMap) => {
      const testRunId = paramMap.get('testRunId');
      const testSuiteId = paramMap.get('testSuiteId');

      if (testSuiteId) {
        return [{ testSuiteId, id: testRunId }];
      }

      return this.assistantKeyResolver.resolveAssistantKeyFromParamMap(paramMap).pipe(
        map((assistantKey) => ({ assistant: assistantKey, id: testRunId })),
      );
    }),
  );

  protected pageName$ = this.testRunKey$.pipe(
    switchMap((request) => {
      if (request.assistant) {
        return getAssistant$({
          id: request.assistant.id,
          namespace: request.assistant.namespace,
        }).pipe(
          map((resp: GetAssistantResponse) => {
            const p = new AssistantNamePipe();
            return p.transform(resp.assistant);
          }),
        );
      }
      return this.testService
        .getTestSuite(request.testSuiteId)
        .pipe(
          map(
            (suiteResp) =>
              this.translateService.instant('AI_ASSISTANT.TESTING.RUN_DISPLAY.TEST_SUITE_PREFIX') +
              ' ' +
              suiteResp.testSuite.name,
          ),
        );
    }),
    shareReplay(1),
  );

  protected testRun$ = combineLatest([this.retry$$.asObservable(), this.testRunKey$]).pipe(
    switchMap(([_, req]: [unknown, GetTestRunV2Request]) =>
      this.testService.getTestRunV2(req.id, {
        assistantKey: req.assistant as AssistantKey,
        testSuiteId: req.testSuiteId,
      }),
    ),
    shareReplay(1),
  );
  protected testRunSig = toSignal(this.testRun$);

  private testResults$: Observable<TestResult[]> = combineLatest([
    this.retry$$.asObservable(),
    this.testRunKey$,
  ]).pipe(
    switchMap(([_, req]: [unknown, GetTestRunV2Request]) =>
      this.testService.fetchAllTestResults(req.id, {
        assistantKey: req.assistant as AssistantKey,
        testSuiteId: req.testSuiteId,
      }),
    ),
    shareReplay(1),
  );
  // Results come from ListTestResults (GetTestRunV2 no longer embeds them). The
  // source-recording lookup reads this instead of the run's embedded results.
  protected testResultsSig = toSignal(this.testResults$);

  isVoiceRun = signal(false);

  dataSource: GalaxyDataSource<TestResult, void, MatSort> = {} as GalaxyDataSource<TestResult, void, MatSort>;

  chatColumns: GalaxyColumnDef[] = [
    { id: 'name', title: 'Name', sortable: true },
    { id: 'status', title: 'Status' },
    { id: 'score', title: 'Score', sortable: true },
    { id: 'model', title: 'Model (Reasoning Effort)' },
    { id: 'latency', title: 'Latency', sortable: true },
    { id: 'agentArchitecture', title: 'Agent Architecture' },
  ];

  voiceColumns: GalaxyColumnDef[] = [
    { id: 'name', title: 'Name', sortable: true },
    { id: 'status', title: 'Status' },
    { id: 'score', title: 'Score', sortable: true },
    { id: 'duration', title: 'Duration', sortable: true },
  ];

  columns = signal<GalaxyColumnDef[]>(this.chatColumns);

  sortData(sort: Sort): void {
    this.dataSource?.setSorting([{ active: sort.active, direction: sort.direction } as MatSort]);
  }

  ngOnInit(): void {
    this.dataSource = new GalaxyDataSource<TestResult, void, MatSort>({
      get: (r) => this.getFiltered(r, this.testResults$),
    });
    this.dataSource.setSorting([{ active: 'name', direction: 'asc' } as MatSort]);

    this.testResults$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((results) => {
      const voice = results.length > 0 && results.every((r) => isVoiceTest(r.testType));
      this.isVoiceRun.set(voice);
      this.columns.set(voice ? this.voiceColumns : this.chatColumns);
    });

    // Load business name if this is an account group namespace
    this.testRunKey$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (request) => {
      if (request.assistant?.namespace?.accountGroupNamespace?.accountGroupId) {
        const businessName = await this.businessNameService.loadBusinessName(
          request.assistant?.namespace.accountGroupNamespace.accountGroupId,
        );
        this.businessName.set(businessName);
      } else {
        this.businessName.set('');
      }
    });
  }

  cancelTestRun() {
    this.testRunKey$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((request) =>
          this.testService.cancelTestRun(request.id, {
            assistantKey: request.assistant as AssistantKey,
            testSuiteId: request.testSuiteId,
          }),
        ),
      )
      .subscribe({
        next: () => {
          this.retry$$.next(undefined);
        },
        error: (err) => {
          console.error('Error cancelling test run:', err);
        },
      });
  }

  retryStuckTestResults() {
    this.testRunKey$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((request) =>
          this.testService.retryStuckTestResults(request.id, {
            assistantKey: request.assistant as AssistantKey,
            testSuiteId: request.testSuiteId,
          }),
        ),
      )
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessSnack(
            this.translateService.instant('AI_ASSISTANT.TESTING.RUN_DISPLAY.RETRYING'),
          );
          this.retry$$.next(undefined);
        },
        error: (err) => {
          console.error('Error retrying stuck test results:', err);
          const message =
            err.status === 412
              ? this.translateService.instant('AI_ASSISTANT.TESTING.RUN_DISPLAY.STILL_RUNNING')
              : err.error?.message || this.translateService.instant('AI_ASSISTANT.TESTING.RUN_DISPLAY.RETRY_ERROR');
          this.snackbarService.openErrorSnack(message);
        },
      });
  }

  getPassedCount(testRun: TestRun): string {
    if (testRun.resultCount > 0) {
      return `${testRun.successCount ?? 0} / ${testRun.resultCount}`;
    }
    if (testRun.isSuccessEvaluation && testRun.status !== 'in_progress') {
      return '0 / 0';
    }
    return this.translateService.instant('AI_ASSISTANT.TESTING.RUN_TABLE.NA');
  }

  sourceRecordingUrl(results: TestResult[] | undefined): string {
    return results?.find((r) => r.recordingUrl)?.recordingUrl ?? '';
  }

  private getFiltered(
    r: PagedListRequestInterface<void, MatSort>,
    source$: Observable<TestResult[]>,
  ): Observable<PagedResponseInterface<TestResult>> {
    return source$.pipe(
      map((allResults: TestResult[]) => {
        let data = [...allResults];
        const pageSize = r.pagingOptions.pageSize || 10;
        const startIndex = +r.pagingOptions.cursor || 0;

        if (r.searchOptions?.text) {
          data = data.filter((testResult) => {
            const searchText = r.searchOptions.text.toLowerCase();
            const nameMatch = testResult.name.toLowerCase().includes(searchText);
            const expectationMatch = testResult.expected?.toLowerCase().includes(searchText) ?? false;
            const chatHistoryMatch =
              testResult.chatHistory?.some((msg) => msg.content.toLowerCase().includes(searchText)) ?? false;
            return nameMatch || expectationMatch || chatHistoryMatch;
          });
        }
        if (r.sorting?.length > 0) {
          const sort = r.sorting[0] as MatSort;
          data.sort((a, b) => {
            if (sort.active === 'score') {
              return sort.direction === 'asc' ? (a.score || 0) - (b.score || 0) : (b.score || 0) - (a.score || 0);
            }
            if (sort.active === 'duration') {
              return sort.direction === 'asc'
                ? (a.callDuration || 0) - (b.callDuration || 0)
                : (b.callDuration || 0) - (a.callDuration || 0);
            }
            if (sort.active === 'latency') {
              return sort.direction === 'asc'
                ? (a.latency || 0) - (b.latency || 0)
                : (b.latency || 0) - (a.latency || 0);
            }
            const aValue = a[sort.active] || '';
            const bValue = b[sort.active] || '';
            return sort.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          });
        }

        const endIndex = startIndex + pageSize;
        const paginatedData = data.slice(startIndex, endIndex);

        return {
          data: paginatedData,
          pagingMetadata: {
            nextCursor: endIndex < data.length ? endIndex.toString() : '',
            hasMore: endIndex < data.length,
            totalResults: data.length,
          },
        };
      }),
    );
  }

  openDialog(testResult: TestResult): void {
    this.dialog.open(TestResultsModalComponent, { data: testResult });
  }

  getModelAndReasoning(testResult: TestResult): string {
    const modelNameLookup = this.models();
    return formatModelAndReasoningFromTestResult(testResult, modelNameLookup);
  }

  getAgentArchitectureName(architecture: AgentArchitecture): string {
    return getAgentArchitectureName(architecture, this.translateService);
  }

  getFormattedCallDuration(durationNanos: number): string {
    return formatCallDuration(durationNanos);
  }
}
